import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkSetDeleted, bulkSetField } from '../../api/channelBulk';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

function normCat(c: string | null | undefined) {
  if (c == null || c === '') return 'UNCATEGORIZED';
  const v = String(c).trim().toUpperCase();
  if (v === 'UNCATEGORIZED') return 'UNCATEGORIZED';
  return v;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function SearchReplaceNameModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();

  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [searchWord, setSearchWord] = useState('');
  const [replaceWith, setReplaceWith] = useState('');
  const [action, setAction] = useState<'delete' | 'remove' | 'replace'>('remove');
  const [useRegex, setUseRegex] = useState(false);
  const [searchMode, setSearchMode] = useState<'spaces' | 'spaces_symbols' | 'always'>('spaces');
  const [searchPosition, setSearchPosition] = useState<'all' | 'first' | 'last'>('all');
  const [firstLetters, setFirstLetters] = useState(7);
  const [lastLetters, setLastLetters] = useState(7);
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [catPick, setCatPick] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of channels) s.add(normCat(c.category));
    return [...s].sort((a, b) => a.localeCompare(b, 'en'));
  }, [channels]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(rows);
      const init: Record<string, boolean> = {};
      for (const c of rows) init[normCat(c.category)] = true;
      setCatPick(init);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to fetch channels.'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setSearchWord('');
    setReplaceWith('');
    setAction('remove');
    setUseRegex(false);
    setSearchMode('spaces');
    setSearchPosition('all');
    setFirstLetters(7);
    setLastLetters(7);
    if (selectedIds.length > 0 && gridActive) setScope('selected');
    else setScope('all');
    void load();
  }, [show, load, selectedIds.length, gridActive]);

  function targetRows(): ChannelRow[] {
    if (scope === 'selected') {
      const set = new Set(selectedIds.map(Number));
      return channels.filter((ch) => set.has(Number(ch.id)));
    }
    if (scope === 'categories') {
      const cats = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const cset = new Set(cats);
      return channels.filter((ch) => cset.has(normCat(ch.category)));
    }
    return channels;
  }

  function buildMatcher(source: string): RegExp {
    if (useRegex) return new RegExp(source, 'g');
    const escaped = escapeRegExp(source);
    if (searchMode === 'always') return new RegExp(escaped, 'g');
    if (searchMode === 'spaces') return new RegExp(`(^|\\s)(${escaped})(?=\\s|$)`, 'g');
    return new RegExp(`(^|[\\s.,;:!?()\\[\\]{}"'\\-_/\\\\|@#$%^&*+=~\`<>])(${escaped})(?=[\\s.,;:!?()\\[\\]{}"'\\-_/\\\\|@#$%^&*+=~\`<>]|$)`, 'g');
  }

  function applyInSegment(segment: string, matcher: RegExp, replacement: string): { changed: boolean; value: string } {
    matcher.lastIndex = 0;
    if (!matcher.test(segment)) return { changed: false, value: segment };
    matcher.lastIndex = 0;
    if (useRegex || searchMode === 'always') {
      const value = segment.replace(matcher, replacement);
      return { changed: value !== segment, value };
    }
    const value = segment.replace(matcher, `$1${replacement}`);
    return { changed: value !== segment, value };
  }

  function transformName(name: string, matcher: RegExp, replacement: string): { changed: boolean; value: string } {
    if (searchPosition === 'all') return applyInSegment(name, matcher, replacement);
    if (searchPosition === 'first') {
      const len = Math.max(1, Number(firstLetters) || 1);
      const left = name.slice(0, len);
      const right = name.slice(len);
      const result = applyInSegment(left, matcher, replacement);
      return { changed: result.changed, value: result.value + right };
    }
    const len = Math.max(1, Number(lastLetters) || 1);
    const cut = Math.max(0, name.length - len);
    const left = name.slice(0, cut);
    const right = name.slice(cut);
    const result = applyInSegment(right, matcher, replacement);
    return { changed: result.changed, value: left + result.value };
  }

  async function apply() {
    if (!playlistId) {
      showToast('Select a playlist first.', 'warning');
      return;
    }
    const pid = parseInt(playlistId, 10);
    if (!searchWord) {
      showToast('Enter text to search for.', 'warning');
      return;
    }
    if (action === 'replace' && !replaceWith) {
      showToast('Enter text in "Replace with".', 'warning');
      return;
    }
    if (scope === 'selected') {
      if (!gridActive || selectedIds.length === 0) {
        showToast('Select channels in the Basic editor.', 'warning');
        return;
      }
    } else if (scope === 'categories') {
      const categoriesScope = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (categoriesScope.length === 0) {
        showToast('Select at least one category.', 'warning');
        return;
      }
    }

    let matcher: RegExp;
    try {
      matcher = buildMatcher(searchWord);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Invalid regex expression.'), 'error');
      return;
    }

    const rows = targetRows();
    const replacement = action === 'replace' ? replaceWith : '';

    if (action === 'delete') {
      const idsToDelete: number[] = [];
      for (const ch of rows) {
        const id = Number(ch.id);
        if (!Number.isFinite(id)) continue;
        const oldName = String(ch.name ?? '');
        if (transformName(oldName, matcher, replacement).changed) idsToDelete.push(id);
      }
      if (idsToDelete.length === 0) {
        showToast('No matching streams found.', 'warning');
        return;
      }
      setBusy(true);
      try {
        const res = await bulkSetDeleted({ playlist_id: pid, ids: idsToDelete, deleted: true });
        if (!res.success) {
          showToast(res.message || 'Update failed.', 'error');
          return;
        }
        showToast(res.message || 'Streams deleted.', 'success');
        record({
          label: 'Search: delete matching streams',
          undo: { type: 'setDeleted', playlist_id: pid, ids: idsToDelete, deleted: false },
          redo: { type: 'setDeleted', playlist_id: pid, ids: idsToDelete, deleted: true },
        });
        onClose();
        await refreshEditor();
      } catch (e) {
        showToast(getApiErrorMessage(e, 'Server error.'), 'error');
      } finally {
        setBusy(false);
      }
      return;
    }

    const undoValuesById: Record<string, string> = {};
    const redoValuesById: Record<string, string> = {};
    for (const ch of rows) {
      const id = Number(ch.id);
      if (!Number.isFinite(id)) continue;
      const oldName = String(ch.name ?? '');
      const result = transformName(oldName, matcher, replacement);
      if (!result.changed || result.value === oldName) continue;
      undoValuesById[id] = oldName;
      redoValuesById[id] = result.value;
    }

    if (Object.keys(redoValuesById).length === 0) {
      showToast('No matching streams found.', 'warning');
      return;
    }

    const updates = Object.entries(redoValuesById).map(([id, value]) => ({ id: parseInt(id, 10), value }));
    setBusy(true);
    try {
      const res = await bulkSetField({ playlist_id: pid, field: 'name', updates });
      if (!res.success) {
        showToast(res.message || 'Update failed.', 'error');
        return;
      }
      showToast(res.message || 'Names updated.', 'success');
      record({
        label: action === 'replace' ? 'Search & replace names' : 'Search: remove words',
        undo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: undoValuesById },
        redo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: redoValuesById },
      });
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Search & replace/remove"
      size="xl"
      footer={
        step === 2 ? (
          <div className="d-flex justify-content-between w-100">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Previous
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" disabled={busy} onClick={() => void apply()}>
                {busy ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>
              Next
            </button>
          </div>
        )
      }
    >
      <div className="progress mb-3" style={{ height: 6 }}>
        <div className="progress-bar bg-primary" style={{ width: step === 1 ? '50%' : '100%' }} />
      </div>

      {step === 1 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 1</p>
          <label className="form-label">Search & replace or remove words in streams name</label>
          <hr className="my-3" />
          <div className="row g-3">
            <div className="col-md-6">
              <div className="mb-2">
                <label className="form-label">Word to search</label>
                <input className="form-control" placeholder="24h" value={searchWord} onChange={(e) => setSearchWord(e.target.value)} />
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srActionDelete" checked={action === 'delete'} onChange={() => setAction('delete')} />
                <label className="form-check-label" htmlFor="srActionDelete">
                  Delete streams containing '' in the name
                </label>
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srActionRemove" checked={action === 'remove'} onChange={() => setAction('remove')} />
                <label className="form-check-label" htmlFor="srActionRemove">
                  Remove '' from streams name
                </label>
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srActionReplace" checked={action === 'replace'} onChange={() => setAction('replace')} />
                <label className="form-check-label" htmlFor="srActionReplace">
                  Replace '' with
                </label>
                <input
                  className="form-control form-control-sm mt-1"
                  style={{ maxWidth: 220 }}
                  disabled={action !== 'replace'}
                  value={replaceWith}
                  onChange={(e) => setReplaceWith(e.target.value)}
                />
              </div>
              <hr className="my-3" />
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="srRegex" checked={useRegex} onChange={(e) => setUseRegex(e.target.checked)} />
                <label className="form-check-label" htmlFor="srRegex">
                  Use regex expression
                </label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-check">
                <input className="form-check-input" type="radio" id="srModeSpaces" checked={searchMode === 'spaces'} onChange={() => setSearchMode('spaces')} />
                <label className="form-check-label" htmlFor="srModeSpaces">
                  Apply if word is wrapped by spaces
                </label>
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srModeSymbols" checked={searchMode === 'spaces_symbols'} onChange={() => setSearchMode('spaces_symbols')} />
                <label className="form-check-label" htmlFor="srModeSymbols">
                  Apply if word is wrapped by spaces and symbols
                </label>
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srModeAlways" checked={searchMode === 'always'} onChange={() => setSearchMode('always')} />
                <label className="form-check-label" htmlFor="srModeAlways">
                  Apply always
                </label>
              </div>
              <hr className="my-3" />
              <div className="form-check">
                <input className="form-check-input" type="radio" id="srPosAll" checked={searchPosition === 'all'} onChange={() => setSearchPosition('all')} />
                <label className="form-check-label" htmlFor="srPosAll">
                  Search in all stream's name
                </label>
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srPosFirst" checked={searchPosition === 'first'} onChange={() => setSearchPosition('first')} />
                <label className="form-check-label" htmlFor="srPosFirst">
                  Search in the first letters:
                </label>
                <input
                  className="form-control form-control-sm mt-1"
                  style={{ maxWidth: 96 }}
                  type="number"
                  min={1}
                  max={100}
                  disabled={searchPosition !== 'first'}
                  value={firstLetters}
                  onChange={(e) => setFirstLetters(Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
              <div className="form-check mt-2">
                <input className="form-check-input" type="radio" id="srPosLast" checked={searchPosition === 'last'} onChange={() => setSearchPosition('last')} />
                <label className="form-check-label" htmlFor="srPosLast">
                  Search in the last letters:
                </label>
                <input
                  className="form-control form-control-sm mt-1"
                  style={{ maxWidth: 96 }}
                  type="number"
                  min={1}
                  max={100}
                  disabled={searchPosition !== 'last'}
                  value={lastLetters}
                  onChange={(e) => setLastLetters(Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 2</p>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="srSel"
              checked={scope === 'selected'}
              disabled={!gridActive || selectedIds.length === 0}
              onChange={() => setScope('selected')}
            />
            <label className="form-check-label" htmlFor="srSel">
              {selectedIds.length} selected streams
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="srCat" checked={scope === 'categories'} onChange={() => setScope('categories')} />
            <label className="form-check-label" htmlFor="srCat">
              Streams from categories
            </label>
          </div>
          <div className="form-check mt-2 mb-2">
            <input className="form-check-input" type="radio" id="srAll" checked={scope === 'all'} onChange={() => setScope('all')} />
            <label className="form-check-label" htmlFor="srAll">
              All streams
            </label>
          </div>

          {scope === 'categories' ? (
            <div className="border rounded p-2 mb-0 bg-light bg-opacity-50" style={{ maxHeight: 160, overflowY: 'auto' }}>
              {categories.map((c) => {
                const display = c.toUpperCase();
                return (
                  <div className="form-check" key={c}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`sr-${c}`}
                      checked={!!catPick[c]}
                      onChange={(e) => setCatPick((p) => ({ ...p, [c]: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor={`sr-${c}`}>
                      {display}
                    </label>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      ) : null}
    </SimpleModal>
  );
}
